import axios from "axios";
import { getApiBaseUrl } from "../utils/apiConfig";

const getAuthHeaders = () => {
  const token = localStorage.getItem("jwt_token") || localStorage.getItem("token") || localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getAdminAuthHeaders = () => {
  const adminToken = localStorage.getItem("adminToken") || localStorage.getItem("jwt_token");
  return adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
};

/**
 * Send a message to the AI Travel Concierge.
 */
export const sendChatMessage = async ({ message, sessionId, sessionToken, guestName, guestEmail }) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.post(
      `${baseUrl}/chat`,
      { message, sessionId, sessionToken, guestName, guestEmail },
      { headers: { "Content-Type": "application/json", ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error("sendChatMessage error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Fetch chat message history for a given session.
 */
export const fetchChatHistory = async (sessionId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/chat/history/${sessionId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("fetchChatHistory error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Initialize a fresh chat session.
 */
export const createNewSession = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.post(
      `${baseUrl}/chat/new-session`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("createNewSession error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Create a new support ticket (user or guest).
 */
export const createSupportTicket = async (ticketData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.post(`${baseUrl}/tickets`, ticketData, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    });
    return response.data;
  } catch (error) {
    console.error("createSupportTicket error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Fetch tickets for the logged-in user or guest email.
 */
export const fetchUserTickets = async (guestEmail) => {
  try {
    const baseUrl = getApiBaseUrl();
    const params = guestEmail ? { guestEmail } : {};
    const response = await axios.get(`${baseUrl}/tickets/user`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("fetchUserTickets error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Admin: Fetch all tickets with filters.
 */
export const fetchAdminTickets = async (params = {}) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/admin/tickets`, {
      params,
      headers: getAdminAuthHeaders(),
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("fetchAdminTickets error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Admin: Update ticket status, priority, admin assignment, and notes.
 */
export const updateAdminTicket = async (ticketId, updateData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.patch(
      `${baseUrl}/admin/tickets/${ticketId}`,
      updateData,
      {
        headers: { "Content-Type": "application/json", ...getAdminAuthHeaders() },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("updateAdminTicket error:", error);
    throw error.response?.data || error;
  }
};

/**
 * Admin: Fetch linked AI chat conversation transcript for a ticket.
 */
export const fetchTicketConversation = async (ticketId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(
      `${baseUrl}/admin/tickets/${ticketId}/conversation`,
      {
        headers: getAdminAuthHeaders(),
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("fetchTicketConversation error:", error);
    throw error.response?.data || error;
  }
};
